import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type IPrivacyPolicyRepository from '@repositories/privacyPolicyRepository/IPrivacyPolicyRepository';
import { PrivacyPolicyPublicModel } from '@repositories/privacyPolicyRepository/models/PrivacyPolicyPublicModel';
import { PrivacyPolicySimpleModel } from '@repositories/privacyPolicyRepository/models/PrivacyPolicySimpleModel';

const privacyPolicyRepo = repository.get<IPrivacyPolicyRepository>(KEYS.PRIVACY_POLICY_REPOSITORY);

export const privacyPolicyQueryKey = {
  all: ['privacyPolicy'] as const,
  lists: () => [...privacyPolicyQueryKey.all, 'list'] as const,
  list: () => [...privacyPolicyQueryKey.lists()] as const,
  details: () => [...privacyPolicyQueryKey.all, 'detail'] as const,
  detail: (version: number, lang: string) => [...privacyPolicyQueryKey.details(), version, lang] as const,
};

/**
 * 개인정보 처리방침 목록 조회
 * GET /api/v1/privacy-policies
 *
 * @description
 * 전체 개인정보 처리방침 버전 목록을 조회합니다.
 * 각 버전의 간략한 정보(버전, 시행일, 상태 등)를 반환합니다.
 */
export const useGetPrivacyPolicyList = (options?: UseQueryOptions<PrivacyPolicySimpleModel[], AxiosError>) => {
  return useQuery({
    queryKey: privacyPolicyQueryKey.list(),
    queryFn: async () => {
      const result = await privacyPolicyRepo.getPrivacyPolicyList();
      return result.map(dto => new PrivacyPolicySimpleModel(dto));
    },
    ...options,
  });
};
/**
 * 특정 버전의 개인정보 처리방침 상세 조회
 * GET /api/v1/privacy-policies/:version?lang={lang}
 *
 * @param version - 조회할 버전 번호
 * @param lang - 언어 코드 (예: 'ko', 'en')
 *
 * @description
 * 특정 버전의 개인정보 처리방침 전체 내용을 조회합니다.
 * 언어별로 다른 내용을 제공할 수 있습니다.
 */
export const useGetPrivacyPolicy = (
  version: number,
  lang: string,
  options?: UseQueryOptions<PrivacyPolicyPublicModel, AxiosError>,
) => {
  return useQuery({
    queryKey: privacyPolicyQueryKey.detail(version, lang),
    queryFn: async () => {
      const result = await privacyPolicyRepo.getPrivacyPolicy(version, lang);
      return new PrivacyPolicyPublicModel(result);
    },
    ...options,
  });
};
